// Medication table — renders medication rows with Pause / Resume / Discontinue actions.
// Discontinue opens a confirmation dialog requiring a stop_reason (DRS P3).

import { useState } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";

export interface Medication {
  _id: string;
  drug_name: string;
  generic_name?: string | null;
  brand_name?: string | null;
  drug_class?: string;
  dosage_mg?: number | null;
  dosage_unit?: string | null;
  dosage?: string;
  frequency: string;
  scheduled_times: string[];
  start_date: string;
  refill_date?: string;
  prescribing_doctor?: string;
  prescribed_by?: string | null;
  status: string;
  is_critical?: boolean;
  interaction_flagged?: boolean;
  notes?: string;
}

export interface MedicationsResponse { data: Medication[]; total: number; page: number; limit: number; }

const FREQ_LABELS: Record<string, string> = {
  once_daily: "Once daily", twice_daily: "Twice daily",
  three_times_daily: "Three times daily", four_times_daily: "Four times daily",
  weekly: "Weekly", as_needed: "As needed", other: "Other",
};

const STATUS_CLS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700", paused: "bg-amber-50 text-amber-700",
  discontinued: "bg-red-50 text-red-700",   completed: "bg-slate-100 text-slate-500",
};

const STOP_REASONS = [
  { value: "",                 label: "Select reason…" },
  { value: "course_completed", label: "Course completed" },
  { value: "side_effects",     label: "Side effects" },
  { value: "doctor_changed",   label: "Doctor changed" },
  { value: "patient_refused",  label: "Patient refused" },
  { value: "improved",         label: "Improved" },
  { value: "other",            label: "Other" },
];

function StatusPill({ status }: { status: string }): JSX.Element {
  const cls = STATUS_CLS[status] ?? "bg-slate-100 text-slate-500";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{status}</span>;
}

function DiscontinueDialog({ medName, isPending, onConfirm, onCancel }: {
  medName: string; isPending: boolean;
  onConfirm: (reason: string) => void; onCancel: () => void;
}): JSX.Element {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4">
        <p className="text-sm font-semibold text-slate-800">Discontinue: {medName}</p>
        <p className="text-xs text-slate-500">This action cannot be undone. Select the reason for discontinuation.</p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Stop reason<span className="text-red-500 ml-0.5">*</span>
          </label>
          <select value={reason} onChange={(e) => { setReason(e.target.value); setErr(""); }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy-500">
            {STOP_REASONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel}
            className="text-sm px-4 py-2 text-slate-600 hover:text-slate-900">Cancel</button>
          <button type="button" disabled={isPending} onClick={() => {
            if (!reason) { setErr("Please select a reason."); return; }
            onConfirm(reason);
          }} className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {isPending ? "Stopping…" : "Confirm discontinue"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MedActions({ med, patientId }: { med: Medication; patientId: string }): JSX.Element {
  const qc = useQueryClient();
  const [discontinuing, setDiscontinuing] = useState(false);

  const patch = useMutation({
    mutationFn: (body: { status: string; stop_reason?: string }) =>
      apiFetch(`/medications/${med._id}/status`, {
        method: "PATCH", body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setDiscontinuing(false);
      void qc.invalidateQueries({ queryKey: ["medications", patientId] });
    },
  });

  const { status } = med;
  return (
    <>
      <div className="flex gap-2">
        {status === "active" && (
          <button onClick={() => patch.mutate({ status: "paused" })} disabled={patch.isPending}
            className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors">
            Pause
          </button>
        )}
        {status === "paused" && (
          <button onClick={() => patch.mutate({ status: "active" })} disabled={patch.isPending}
            className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition-colors">
            Resume
          </button>
        )}
        {(status === "active" || status === "paused") && (
          <button onClick={() => setDiscontinuing(true)} disabled={patch.isPending}
            className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors">
            Discontinue
          </button>
        )}
      </div>
      {discontinuing && (
        <DiscontinueDialog
          medName={med.drug_name}
          isPending={patch.isPending}
          onConfirm={(reason) => patch.mutate({ status: "discontinued", stop_reason: reason })}
          onCancel={() => setDiscontinuing(false)}
        />
      )}
    </>
  );
}

function dosageDisplay(m: Medication): string {
  if (m.dosage_mg != null && m.dosage_unit) return `${m.dosage_mg} ${m.dosage_unit}`;
  return m.dosage ?? "—";
}

export function MedTable({ items, patientId }: { items: Medication[]; patientId: string }): JSX.Element {
  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">No medications recorded. Add the first one.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
            <th className="py-3 pr-4 font-medium">Drug Name</th>
            <th className="py-3 pr-4 font-medium">Dose</th>
            <th className="py-3 pr-4 font-medium">Frequency</th>
            <th className="py-3 pr-4 font-medium">Scheduled Times</th>
            <th className="py-3 pr-4 font-medium">Status</th>
            <th className="py-3 pr-4 font-medium">Prescribing Doctor</th>
            <th className="py-3 pr-4 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {items.map((m) => (
            <tr key={m._id} className="hover:bg-navy-50 transition-colors">
              <td className="py-3 pr-4 font-medium text-slate-800">
                <span>{m.drug_name}</span>
                {m.generic_name && <span className="block text-xs text-slate-400">{m.generic_name}</span>}
                {m.brand_name && <span className="ml-1 text-xs text-slate-400">({m.brand_name})</span>}
                {m.is_critical && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">⚠ Critical</span>}
                {m.interaction_flagged && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Interaction</span>}
              </td>
              <td className="py-3 pr-4 text-slate-600">{dosageDisplay(m)}</td>
              <td className="py-3 pr-4 text-slate-600">{FREQ_LABELS[m.frequency] ?? m.frequency}</td>
              <td className="py-3 pr-4 text-slate-500 font-mono text-xs">{m.scheduled_times.join(", ") || "—"}</td>
              <td className="py-3 pr-4"><StatusPill status={m.status} /></td>
              <td className="py-3 pr-4 text-slate-600">{m.prescribing_doctor ?? m.prescribed_by ?? "—"}</td>
              <td className="py-3 pr-4"><MedActions med={m} patientId={patientId} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
